import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from '../store';
import axios from '../../resources/axios';
import { Parking, ParkingsState, ParkingFormData } from '../../resources/types/parkingsTypes';
import { handleDefaultErrors } from '../../resources/functions';

const initialState: ParkingsState = {
  parkings: [],
  loading: false,
  shouldUpdate: true,
};

export const parkingsSlice = createSlice({
  name: 'parkings',
  initialState,
  reducers: {
    setParkings: (state, action: PayloadAction<Parking[]>) => {
      state.parkings = action.payload;
      state.shouldUpdate = false;
      state.loading = false;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setShouldUpdate: (state, action: PayloadAction<boolean>) => {
      state.shouldUpdate = action.payload;
    },
  },
});

const { setParkings, setLoading, setShouldUpdate } = parkingsSlice.actions;

export const getParkingsAsync = (forceUpdate?: boolean): AppThunk => async (dispatch, getState) => {
  if (!getState().parkings.shouldUpdate && !forceUpdate) return;
  try {
    dispatch(setLoading(true));
    const response = await axios.get('/parkings');
    dispatch(setParkings(response.data.data || []));
  } catch (error) {
    dispatch(setLoading(false));
    handleDefaultErrors(error);
  }
};

export const createParkingAsync = (
  data: ParkingFormData,
  onSuccess?: () => void
): AppThunk => async dispatch => {
  try {
    dispatch(setLoading(true));
    await axios.post('/parkings', data);
    dispatch(setShouldUpdate(true));
    await dispatch(getParkingsAsync(true));
    onSuccess?.();
  } catch (error) {
    dispatch(setLoading(false));
    handleDefaultErrors(error);
  }
};

export const updateParkingAsync = (
  id: string,
  data: Partial<ParkingFormData>,
  onSuccess?: () => void
): AppThunk => async dispatch => {
  try {
    dispatch(setLoading(true));
    await axios.put(`/parkings/${id}`, data);
    dispatch(setShouldUpdate(true));
    await dispatch(getParkingsAsync(true));
    onSuccess?.();
  } catch (error) {
    dispatch(setLoading(false));
    handleDefaultErrors(error);
  }
};

export const deleteParkingAsync = (
  id: string,
  onSuccess?: () => void
): AppThunk => async dispatch => {
  try {
    dispatch(setLoading(true));
    await axios.delete(`/parkings/${id}`);
    dispatch(setShouldUpdate(true));
    await dispatch(getParkingsAsync(true));
    onSuccess?.();
  } catch (error) {
    dispatch(setLoading(false));
    handleDefaultErrors(error);
  }
};

export const uploadParkingPhotoAsync = async (file: File): Promise<string | null> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post('/files/persistent', formData);
    console.log('Upload response:', response.data);
    return response.data.path || response.data.filename || response.data;
  } catch (error) {
    console.error('Upload error:', error);
    handleDefaultErrors(error);
    return null;
  }
};

export const selectParkings = (state: RootState) => state.parkings;

export default parkingsSlice.reducer;
